import { Link } from '@tanstack/react-router'
import { useId, useState } from 'react'
import { replyTextLimit, type QueueItemDetail } from '@rp/contracts'
import { Button, Card } from '../../design'
import { canReviewCorpus } from '../../session/roles'
import { useReadySession } from '../../session/useSession'
import { KIND_LABELS } from '../queue/labels'
import { canEditDraft, type ReplyAccess } from './access'
import { REPLY_COPY } from './copy'
import { replyLength } from './limits'
import { ReplyLengthMeter } from './ReplyLengthMeter'
import { ReplySendStatus } from './ReplySendStatus'
import { SendConfirmSheet } from './SendConfirmSheet'
import type { ReplyDraft } from './useReplyDraft'

const AccessHint = ({ access }: { access: ReplyAccess }) => {
  if (access.kind === 'claim_required') return <p className="composer-hint tone-warning">{REPLY_COPY.claimRequired}</p>
  if (access.kind === 'held_by_other') {
    return <p className="composer-hint tone-warning">{REPLY_COPY.heldByOther(access.holder)}</p>
  }
  return null
}

const ComposerHints = ({
  access,
  allowed,
  deadlineExpired,
  admitsToCorpus,
}: {
  access: ReplyAccess
  allowed: boolean
  deadlineExpired: boolean
  admitsToCorpus: boolean
}) => (
  <>
    <AccessHint access={access} />
    {allowed && deadlineExpired ? <p className="composer-hint tone-warning">{REPLY_COPY.deadlineExpired}</p> : null}
    {admitsToCorpus ? (
      <p className="composer-hint tone-info">
        <span>{REPLY_COPY.corpusNotice}</span> <Link to="/corpus">{REPLY_COPY.corpusLink}</Link>
      </p>
    ) : null}
  </>
)

const destinationOf = (item: QueueItemDetail): string =>
  item.kind === 'dm'
    ? REPLY_COPY.privateDestination(item.channelName, item.authorName)
    : REPLY_COPY.publicDestination(item.channelName, KIND_LABELS[item.kind])

type ReplyComposerProps = {
  item: QueueItemDetail
  access: ReplyAccess
  draft: ReplyDraft
  deadlineExpired?: boolean
}

export const ReplyComposer = ({ item, access, draft, deadlineExpired = false }: ReplyComposerProps) => {
  const { membership } = useReadySession()
  const editorId = useId()
  const [confirming, setConfirming] = useState(false)
  const { text, acceptedSuggestionId, staleDraft, edit, acceptSuggestion, send, pending, state } = draft

  const readySuggestion = item.suggestion?.status === 'ready' ? item.suggestion : null
  const allowed = canEditDraft(membership.role, access)
  const sendable = allowed && access.kind === 'open'
  const length = replyLength(text, replyTextLimit(item.kind, item.platform))
  const destination = destinationOf(item)

  return (
    <Card as="section" className="composer">
      <span className="hatch composer-hatch" aria-hidden="true" />
      <h2 className="composer-title">{REPLY_COPY.title}</h2>
      <p className="composer-destination">{destination}</p>
      <label className="visually-hidden" htmlFor={editorId}>
        {REPLY_COPY.editorLabel}
      </label>
      <textarea
        id={editorId}
        className="composer-editor"
        aria-label={REPLY_COPY.editorLabel}
        placeholder={REPLY_COPY.placeholder}
        value={text}
        rows={5}
        disabled={!allowed}
        onChange={(event) => edit(event.target.value)}
      />
      {staleDraft ? <p className="composer-hint tone-warning">{REPLY_COPY.staleDraft}</p> : null}
      <div className="composer-actions">
        {readySuggestion === null || readySuggestion.text === null ? null : (
          <Button
            className="composer-accept"
            onClick={() => acceptSuggestion({ id: readySuggestion.id, text: readySuggestion.text ?? '' })}
            disabled={!allowed}
          >
            {REPLY_COPY.accept}
          </Button>
        )}
        <ReplyLengthMeter length={length} />
        <Button
          className="composer-send"
          variant="irreversible"
          size="large"
          disabled={!sendable || length.count === 0 || length.overLimit}
          pending={pending}
          pendingLabel={REPLY_COPY.sending}
          onClick={() => setConfirming(true)}
        >
          {REPLY_COPY.send}
        </Button>
      </div>
      <ComposerHints
        access={access}
        allowed={allowed}
        deadlineExpired={deadlineExpired}
        admitsToCorpus={canReviewCorpus(membership.role)}
      />
      <ReplySendStatus state={state} kind={item.kind} onRetry={() => setConfirming(true)} />
      <SendConfirmSheet
        open={confirming}
        kind={item.kind}
        destination={destination}
        text={text}
        pending={pending}
        onCancel={() => setConfirming(false)}
        onConfirm={() => {
          setConfirming(false)
          send(text, acceptedSuggestionId)
        }}
      />
    </Card>
  )
}
